import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allArticles } from '@/mocks/articles';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="max-w-3xl mx-auto px-4 py-20 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 transition-colors cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = allArticles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <article className="py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors cursor-pointer">{article.category}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 truncate max-w-[200px]">{article.title}</span>
            </nav>

            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-50 text-primary-600 mb-4 whitespace-nowrap">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-3 text-sm text-foreground-400 mb-8">
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}阅读</span>
            </div>

            <div className="relative aspect-[16/9] rounded-2xl overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-14 pt-8 border-t border-background-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-foreground-400">分类</p>
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
                  >
                    {article.category}
                  </Link>
                </div>
                <div className="flex gap-2">
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer" title="分享到微博">
                    <i className="ri-weibo-line"></i>
                  </span>
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer" title="分享到微信">
                    <i className="ri-wechat-line"></i>
                  </span>
                  <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer" title="复制链接">
                    <i className="ri-link"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">相关文章</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        title={ra.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-foreground-400 mb-2">{ra.date}</p>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            author: { '@type': 'Organization', name: '樱境物语' },
            publisher: { '@type': 'Organization', name: '樱境物语' },
            image: article.image,
            url: `https://6shouyou.cn/article/${article.slug}`,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://6shouyou.cn/' },
              { '@type': 'ListItem', position: 2, name: article.category, item: `https://6shouyou.cn/category/${article.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: article.title, item: `https://6shouyou.cn/article/${article.slug}` },
            ],
          }),
        }}
      />
    </>
  );
}